// Pure decision logic for the global "organization pending deletion" banner (#27).
// Extracted from TenantDeletionBanner so the self-gating rules can be unit-tested
// in a node environment (no React / i18n needed). Date formatting stays in the
// component; this helper only parses `purgeAfter` into a Date.

/**
 * Decide whether (and how) to render the tenant-deletion banner for a given
 * `activeTenant` (from `GET /users/me`'s `activeTenant`).
 *
 * Contract:
 *   - Returns `null` when there is no tenant or its status is anything other
 *     than a deletion state (self-gating — callers can render unconditionally).
 *   - `status === 'pending_deletion'` → `{ variant: 'pending', purgeDate }`
 *     where `purgeDate` is the parsed `purgeAfter` Date, or `null` when
 *     `purgeAfter` is missing/unparseable (component falls back to a dateless
 *     message).
 *   - `status === 'deleted'` (the pre-existing soft-deactivated state) →
 *     `{ variant: 'deactivated' }` (no date).
 *
 * @param {{status?: string, purgeAfter?: string}|null|undefined} activeTenant
 * @returns {{variant: 'pending', purgeDate: Date|null}|{variant: 'deactivated'}|null}
 */
export function tenantBannerState(activeTenant) {
  const status = activeTenant?.status
  if (status === 'pending_deletion') {
    return { variant: 'pending', purgeDate: parsePurgeDate(activeTenant.purgeAfter) }
  }
  if (status === 'deleted') {
    return { variant: 'deactivated' }
  }
  return null
}

/**
 * Parse an ISO `purgeAfter` string into a Date, returning `null` for
 * missing/non-string/unparseable input.
 *
 * @param {unknown} purgeAfter
 * @returns {Date|null}
 */
function parsePurgeDate(purgeAfter) {
  if (typeof purgeAfter !== 'string' || purgeAfter.trim() === '') return null
  const date = new Date(purgeAfter)
  return Number.isNaN(date.getTime()) ? null : date
}
